// Settings tab (IT only; SPEC.md, Settings and permissions): the it_settings
// values, edited in place one row at a time with an editor for the row's value
// type. The server validates every save and refuses a template token that isn't
// allowed. Permissions have their own tab.

// REACT IMPORTS
import React, { useCallback, useEffect, useMemo, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Alert, CircularProgress, IconButton, MenuItem, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import TuneIcon from "@mui/icons-material/Tune";

// API, CONSTANTS
import { getAPICall, patchAPICall } from "../../../config/apiCalls";
import { apiError } from "../itBudgetUtils";

const GROUPS = ["Matching", "Collector", "Schedules", "Leases", "Copy Year", "Folders", "Names", "Notifications"];
const COLUMNS = 6;
const TIME_ZONES = ["America/Los_Angeles", "America/Denver", "America/Phoenix", "America/Chicago", "America/New_York", "UTC"];
const SAMPLE_TOKENS = {
	"{date}": "2026-09-14", "{vendor}": "Anthropic", "{total}": "50.00", "{invoice}": "66232", "{type}": "Consulting",
	"{cardholder_first}": "Mick", "{cardholder_last}": "Fandrich", "{card_last4}": "9564", "{start}": "Aug 29, 2026", "{end}": "Sep 27, 2026", "{MM}": "10", "{Mon}": "Oct",
};

const tokensOf = (setting) => String(setting.allowed_tokens || "").split(",").map((t) => t.trim()).filter(Boolean);
const samplePreview = (value) => String(value).replace(/\{[^{}]*\}/g, (token) => SAMPLE_TOKENS[token] ?? token);

const SettingsTab = ({ lookups }) => {
	const { instance, accounts } = useMsal();
	const [settings, setSettings] = useState([]);
	const [editingKey, setEditingKey] = useState(null);
	const [value, setValue] = useState("");
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [rowError, setRowError] = useState("");
	const [saved, setSaved] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setSettings((await getAPICall(instance, accounts[0], "/api/it-budget/settings")).data);
		} catch (err) {
			setError(apiError(err, "Couldn't load settings."));
		} finally {
			setLoading(false);
		}
	}, [instance, accounts]);

	useEffect(() => {
		load();
	}, [load]);

	const startEdit = (s) => {
		setEditingKey(s.setting_key);
		setValue(s.setting_value);
		setRowError("");
	};

	const cancel = () => {
		setEditingKey(null);
		setRowError("");
	};

	const save = async (s) => {
		if (value === s.setting_value) return cancel();
		setSaving(true);
		setRowError("");
		try {
			const res = await patchAPICall(instance, accounts[0], `/api/it-budget/settings/${encodeURIComponent(s.setting_key)}`, { value });
			setSettings(res.data.settings);
			setEditingKey(null);
			setSaved(`${s.setting_key} saved. It takes effect on the next request or collector run.`);
		} catch (err) {
			setRowError(apiError(err, "Couldn't save the setting."));
		} finally {
			setSaving(false);
		}
	};

	const onKeyDown = (s) => (e) => {
		if (e.key === "Escape") cancel();
		if (e.key === "Enter" && !e.shiftKey && s.value_type !== "email_list" && s.value_type !== "list") {
			e.preventDefault();
			save(s);
		}
	};

	const grouped = useMemo(() => GROUPS.map((g) => ({ group: g, rows: settings.filter((s) => s.setting_group === g) })).filter((g) => g.rows.length), [settings]);

	// The in-row editor for one value type; every value is still validated on the server.
	const editor = (s) => {
		const common = { size: "small", fullWidth: true, autoFocus: true, value, onChange: (e) => setValue(e.target.value), onKeyDown: onKeyDown(s), error: Boolean(rowError) };
		switch (s.value_type) {
			case "timezone":
				return (
					<TextField select {...common}>
						{[...new Set([value, ...TIME_ZONES])].map((z) => (
							<MenuItem key={z} value={z}>
								{z}
							</MenuItem>
						))}
					</TextField>
				);
			case "office":
				return (
					<TextField select {...common}>
						{(lookups.offices || []).map((o) => (
							<MenuItem key={o.id} value={o.state}>
								{o.state} ({o.city})
							</MenuItem>
						))}
					</TextField>
				);
			case "time":
				return <TextField type="time" {...common} />;
			case "integer":
			case "decimal":
				return <TextField inputMode="decimal" {...common} placeholder={s.value_type === "integer" ? "Whole number" : "e.g. 0.05"} />;
			case "email_list":
			case "list":
				return <TextField multiline minRows={2} {...common} placeholder="Separate entries with commas" />;
			case "dropbox_folder":
				return <TextField {...common} placeholder="/Accounting/Visa Receipts" inputProps={{ className: "itBudgetMono" }} />;
			default:
				return <TextField {...common} inputProps={{ className: "itBudgetMono" }} />;
		}
	};

	if (loading) {
		return (
			<div className="itBudgetEmpty">
				<CircularProgress />
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div className="itBudgetBanner">
				<TuneIcon fontSize="small" style={{ color: "#184c93" }} />
				<span>
					IT only. Click the pencil on a row to change its value. A saved value takes effect on the next request; the Invoice Collector picks it up at the start of its next run. The collector's 08:00 start time is set in AWS EventBridge and is shown here for reference.
				</span>
			</div>
			{error && <div className="itBudgetError">{error}</div>}
			<div className="itBudgetTableContainer">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Key</TableCell>
							<TableCell sx={{ width: "32%" }}>Value</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Description</TableCell>
							<TableCell>Changed</TableCell>
							<TableCell align="right" sx={{ width: 96 }} />
						</TableRow>
					</TableHead>
					<TableBody>
						{grouped.map(({ group, rows }) => (
							<React.Fragment key={group}>
								<TableRow className="itBudgetGroupRow">
									<TableCell colSpan={COLUMNS}>{group}</TableCell>
								</TableRow>
								{rows.map((s) => {
									const editing = editingKey === s.setting_key;
									return (
										<React.Fragment key={s.setting_key}>
											<TableRow className={editing ? "itBudgetSelectedRow" : undefined}>
												<TableCell className="itBudgetMono">{s.setting_key}</TableCell>
												<TableCell className="itBudgetMono">
													{editing ? editor(s) : <b>{s.setting_value === "" ? "(empty)" : s.setting_value}</b>}
												</TableCell>
												<TableCell className="itBudgetMuted">{s.value_type.replace("_", " ")}</TableCell>
												<TableCell>{s.description}</TableCell>
												<TableCell className="itBudgetMuted">{s.changed_by}</TableCell>
												<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
													{editing ? (
														<>
															<Tooltip title="Save">
																<span>
																	<IconButton size="small" color="primary" aria-label="Save setting" disabled={saving} onClick={() => save(s)}>
																		{saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
																	</IconButton>
																</span>
															</Tooltip>
															<Tooltip title="Cancel">
																<IconButton size="small" aria-label="Cancel" disabled={saving} onClick={cancel}>
																	<CloseIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														</>
													) : (
														<Tooltip title="Edit">
															<IconButton size="small" aria-label={`Edit ${s.setting_key}`} disabled={saving} onClick={() => startEdit(s)}>
																<EditIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
												</TableCell>
											</TableRow>
											{editing && (s.value_type === "template" || s.value_type === "dropbox_folder" || rowError) && (
												<TableRow className="itBudgetSelectedRow">
													<TableCell />
													<TableCell colSpan={COLUMNS - 1}>
														{s.value_type === "template" && (
															<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 16px" }}>
																<span className="itBudgetOverline">Allowed tokens</span>
																<span className="itBudgetTokenRow">
																	{tokensOf(s).length ? tokensOf(s).map((t) => <span key={t} className="itBudgetToken">{t}</span>) : <span className="itBudgetMuted">None</span>}
																</span>
																<span className="itBudgetOverline">Preview</span>
																<span className="itBudgetMono">{samplePreview(value)}</span>
																<span className="itBudgetMuted">Applies to new files only; a token not in the list is refused on save.</span>
															</div>
														)}
														{s.value_type === "dropbox_folder" && (
															<span className="itBudgetMuted">
																A folder in the J2 team Dropbox that already exists. Saving checks it and keeps its Dropbox ID, so renaming or moving it later
																doesn't break anything. Folders already registered by ID keep being used; new folders follow the new setting.
															</span>
														)}
														{rowError && <div className="itBudgetError" style={{ marginTop: 4 }}>{rowError}</div>}
													</TableCell>
												</TableRow>
											)}
										</React.Fragment>
									);
								})}
							</React.Fragment>
						))}
						<TableRow>
							<TableCell className="itBudgetMono itBudgetMuted">collector.schedule</TableCell>
							<TableCell className="itBudgetMuted">Daily 08:00</TableCell>
							<TableCell className="itBudgetMuted">read-only</TableCell>
							<TableCell className="itBudgetMuted">EventBridge rule in AWS; change it there</TableCell>
							<TableCell className="itBudgetMuted">—</TableCell>
							<TableCell />
						</TableRow>
					</TableBody>
				</Table>
			</div>
			<Snackbar open={Boolean(saved)} autoHideDuration={4000} onClose={() => setSaved("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert severity="success" onClose={() => setSaved("")}>
					{saved}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default SettingsTab;
